import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Download,
  ExternalLink,
  Copy,
  Share2,
  Calendar,
  FileText,
  BarChart3,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Clock,
  Edit3,
  ChevronDown,
  Eye,
  MessageSquare,
  Linkedin,
  Image,
} from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import SurveyQuestionsPreview from './SurveyQuestionsPreview';
import SurveyValidationModal from './SurveyValidationModal';
import { useSurveyEditor, type EditorPending } from './useSurveyEditor';
import type { ValidationReport } from '../../utils/surveyValidation';
import type { SurveyAuthoringSettings } from './surveyAuthoringSettings';
import {
  getSurveyGroupDetail,
  getSurveyGroupResponses,
  getSurveyGroupExportUrl,
  updateSurveyGroupQuestions,
  validateSurveyGroup,
  surveyIdToShareId,
  SurveyGroup,
  type SurveyBlockDefinition,
  getLinkedInShareUrl,
} from '../../utils/surveyApi';
import LinkedInVerificationsTable from './LinkedInVerificationsTable';
import { getGroupColor } from '../../components/survey/GroupMediaUpload';
import { formatDuration } from '../../lib/utils';

interface ResponseEntry {
  session_id: number;
  completed_at: string | null;
  responses_count: number;
  time_spent_seconds: number | null;
  quality_summary: {
    total_responses: number;
    flagged_responses: number;
    flag_types: Record<string, number>;
    quality_score: number;
  } | null;
  group_label: string;
  group_index: number;
}

const SurveyGroupDetails: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  const [group, setGroup] = useState<SurveyGroup | null>(null);
  const [sessionCounts, setSessionCounts] = useState<
    Record<number, { total: number; completed: number }>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [_copied, setCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPreviewPicker, setShowPreviewPicker] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Quality data
  const [allResponses, setAllResponses] = useState<ResponseEntry[]>([]);
  const [perGroupResponses, setPerGroupResponses] = useState<
    Record<number, { label: string; responses: ResponseEntry[] }>
  >({});
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<number | 'all'>('all');

  // Question editing
  const [isEditingQuestions, setIsEditingQuestions] = useState(false);
  const [isSavingQuestions, setIsSavingQuestions] = useState(false);

  // Allocation ratios
  const [allocationRatios, setAllocationRatios] = useState<number[]>([]);
  const [isEditingRatios, setIsEditingRatios] = useState(false);
  const [isSavingRatios, setIsSavingRatios] = useState(false);

  // LinkedIn verification state
  const [showLinkedInVerifications, setShowLinkedInVerifications] = useState(false);

  // Welcome screen editing (title, description, instructions)
  const [welcomeData, setWelcomeData] = useState<{
    title?: string;
    description?: string;
    instructions?: string;
  }>({});
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedInstructions, setEditedInstructions] = useState('');
  const [isEditingWelcome, setIsEditingWelcome] = useState(false);
  const [isSavingWelcome, setIsSavingWelcome] = useState(false);

  useEffect(() => {
    if (!groupId) return;

    const fetchData = async (isInitial = false) => {
      try {
        if (isInitial) setIsLoading(true);
        const [detailData, responsesData] = await Promise.all([
          getSurveyGroupDetail(parseInt(groupId)),
          getSurveyGroupResponses(parseInt(groupId)).catch(() => null),
        ]);
        setGroup(detailData.group);
        if (
          isInitial &&
          Array.isArray(detailData.group.questions) &&
          detailData.group.questions.length > 0
        ) {
          // Best-effort badge population; failure must never block loading the group.
          try {
            const { report } = await validateSurveyGroup(
              detailData.group.id,
              detailData.group.questions,
              detailData.group.blocks ?? undefined,
            );
            validation.setReport(report);
          } catch {
            /* badges are a convenience */
          }
        }
        setSessionCounts(detailData.session_counts);
        if (detailData.group.allocation_ratios) {
          setAllocationRatios(detailData.group.allocation_ratios);
        } else {
          setAllocationRatios(new Array(detailData.group.group_count).fill(1));
        }
        // Set welcome data from API response. Only seed the editable fields on the INITIAL load —
        // the 30s poll must not overwrite an admin's in-progress edits with stale server values.
        if (detailData.welcome_data) {
          setWelcomeData(detailData.welcome_data);
          if (isInitial) {
            setEditedTitle(detailData.welcome_data.title || '');
            setEditedDescription(detailData.welcome_data.description || '');
            setEditedInstructions(detailData.welcome_data.instructions || '');
          }
        }
        if (responsesData) {
          setAllResponses(responsesData.responses);
          setPerGroupResponses(responsesData.per_group);
        }
      } catch (e: any) {
        if (isInitial) {
          setError(e.message || 'Failed to load survey group');
        }
      } finally {
        if (isInitial) setIsLoading(false);
      }
    };

    fetchData(true); // Initial fetch

    // Poll every 30 seconds for updates
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [groupId]);

  const distributionUrl = group
    ? `${import.meta.env.VITE_API_URL}/api/survey-groups/distribute/${group.id}`
    : '';

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(distributionUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [distributionUrl]);

  const handleShare = () => setShowShareModal(true);

  const handleExportXlsx = useCallback(async () => {
    if (!group) return;
    try {
      setIsExporting(true);
      const url = getSurveyGroupExportUrl(group.id);
      const response = await fetch(url, { method: 'GET', credentials: 'include' });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const disposition = response.headers.get('Content-Disposition');
      link.download =
        disposition?.match(/filename="([^"]+)"/)?.[1] || `${group.name}_responses.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setIsExporting(false);
    }
  }, [group]);

  const handleSaveRatios = useCallback(async () => {
    if (!group) return;
    try {
      setIsSavingRatios(true);
      const result = await updateSurveyGroupQuestions(
        group.id,
        group.questions,
        undefined,
        undefined,
        allocationRatios,
      );
      setGroup(result.group);
      setIsEditingRatios(false);
    } catch (e: any) {
      console.error('Failed to save allocation ratios:', e);
      setError('Failed to save allocation ratios.');
    } finally {
      setIsSavingRatios(false);
    }
  }, [group, allocationRatios]);

  // Save-always editor (shared with SurveyDetails via the hook): edits persist immediately and the
  // returned report drives the per-question badges. Only a destructive live-response edit gates.
  const validation = useSurveyEditor<
    EditorPending,
    { group: SurveyGroup; report?: ValidationReport | null }
  >({
    validate: (questions, blocks, opts) =>
      validateSurveyGroup(group!.id, questions as unknown[], blocks ?? undefined, opts),
    save: (pending, ack) => {
      // One "Adaptive features" switch drives all three adaptive flags across the group's child
      // surveys; the legacy redundancy-skip system is disabled.
      const adaptive = pending.adaptive_probing as boolean;
      return updateSurveyGroupQuestions(
        group!.id,
        pending.questions,
        pending.redirect_config,
        pending.max_responses as number | null,
        undefined,
        pending.blocks as SurveyBlockDefinition[] | null,
        ack,
        adaptive,
        adaptive,
        adaptive,
        false,
        undefined,
        pending.survey_level_probing_enabled as boolean,
      );
    },
    applyResult: (result) => setGroup(result.group),
    extractReport: (result) => result.report ?? null,
    onError: (msg) => setError(msg),
  });

  const handleSaveWelcome = useCallback(async () => {
    if (!group) return;
    try {
      setIsSavingWelcome(true);
      setError(null);
      const result = await updateSurveyGroupQuestions(
        group.id,
        group.questions,
        undefined, // redirectConfig
        undefined, // maxResponses
        undefined, // allocationRatios
        undefined, // blocks
        undefined, // acknowledgeWarnings
        undefined, // adaptiveProbing
        undefined, // crossQuestionCoverage
        undefined, // adaptiveQuestionScoping
        undefined, // enableAiRedundancySkip
        { title: editedTitle, description: editedDescription, instructions: editedInstructions }, // welcomeData
      );
      setGroup(result.group);
      setWelcomeData({
        title: editedTitle,
        description: editedDescription,
        instructions: editedInstructions,
      });
      setIsEditingWelcome(false);
    } catch (e: any) {
      console.error('Failed to save welcome screen:', e);
      setError('Failed to save welcome screen. Please try again.');
    } finally {
      setIsSavingWelcome(false);
    }
  }, [group, editedTitle, editedDescription, editedInstructions]);

  const handleQuestionsUpdated = useCallback(
    async (updatedQuestions: any[], settings: SurveyAuthoringSettings) => {
      if (!group) return;
      setIsSavingQuestions(true);
      setError(null);
      // Spread rather than re-list, so a new setting reaches `save` without a second edit here.
      // The two the group endpoints cannot store yet arrive as `undefined` — the editor does not
      // offer their controls on this surface, so there is no value of the author's to lose. See
      // `restrictedOn` in surveyAuthoringSettings (BluePill-AI/backend#829).
      await validation.saveQuestions({ questions: updatedQuestions, ...settings });
      setIsSavingQuestions(false);
    },
    [group, validation],
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Compute filtered responses with useMemo for proper reactivity
  const filteredResponses = useMemo(() => {
    if (selectedGroupFilter === 'all') {
      return allResponses;
    }
    return Object.entries(perGroupResponses)
      .filter(([, v]) => v.label === group?.group_labels[selectedGroupFilter as number])
      .flatMap(([, v]) => v.responses);
  }, [selectedGroupFilter, allResponses, perGroupResponses, group?.group_labels]);

  // Compute quality stats with useMemo for proper reactivity
  const qualityStats = useMemo(() => {
    if (filteredResponses.length === 0) {
      return { total: 0, qualityScore: 0, flagged: 0, avgTime: null, issues: {} };
    }
    let totalQuality = 0;
    let flagged = 0;
    let totalTime = 0;
    let timeCount = 0;
    const issues: Record<string, number> = {};

    let qualityCount = 0;
    filteredResponses.forEach((r) => {
      if (r.quality_summary) {
        totalQuality += r.quality_summary.quality_score;
        qualityCount++;
        if (r.quality_summary.flagged_responses > 0) flagged++;
        Object.entries(r.quality_summary.flag_types).forEach(([flag, count]) => {
          issues[flag] = (issues[flag] || 0) + count;
        });
      }
      if (r.time_spent_seconds != null) {
        totalTime += r.time_spent_seconds;
        timeCount++;
      }
    });

    return {
      total: filteredResponses.length,
      // Average over responses that were actually scored, not the full response count —
      // unscored responses (null quality_summary) must not dilute the score toward zero.
      qualityScore: qualityCount > 0 ? totalQuality / qualityCount : 0,
      flagged,
      avgTime: timeCount > 0 ? totalTime / timeCount : null,
      issues,
    };
  }, [filteredResponses]);

  const getQualityScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualityLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Attention';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading survey group...</p>
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-6 text-center py-8">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Survey Group Not Found</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button
              variant="primary"
              onClick={() => navigate('/surveys')}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              Back to Surveys
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!group) return null;

  const totalCompleted = Object.values(sessionCounts).reduce((s, c) => s + c.completed, 0);
  const totalQuestions = group.questions?.length || 0;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header — same as SurveyDetails */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/surveys')}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              Back to Surveys
            </Button>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleExportXlsx}
                disabled={isExporting}
                icon={<Download className="w-4 h-4" />}
              >
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </Button>
              {group.survey_ids && group.survey_ids.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/surveys/${group.survey_ids[0]}/chat`)}
                  icon={<MessageSquare className="w-4 h-4" />}
                >
                  Chat with Survey
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    `/surveys/images?surveyIds=${group.survey_ids.join(',')}&groupId=${group.id}`,
                  )
                }
                icon={<Image className="w-4 h-4" />}
              >
                View Images
              </Button>
              <Button variant="primary" onClick={handleShare} icon={<Share2 className="w-4 h-4" />}>
                Share
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Survey Overview — same card as SurveyDetails */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <FileText className="w-6 h-6 text-blue-500 mr-2" />
                    <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      active
                    </span>
                    <a
                      href={distributionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View Live
                    </a>
                    {group.survey_ids?.length > 0 && (
                      <div className="relative">
                        <button
                          onClick={() => setShowPreviewPicker((p) => !p)}
                          className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview Survey
                          <ChevronDown className="w-3 h-3 ml-0.5" />
                        </button>
                        {showPreviewPicker && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setShowPreviewPicker(false)}
                            />
                            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[160px]">
                              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 border-b border-gray-100">
                                Select group to preview
                              </div>
                              {group.survey_ids.map((sid, idx) => (
                                <a
                                  key={sid}
                                  href={`/surveys/${surveyIdToShareId(sid)}/take?preview=true`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setShowPreviewPicker(false)}
                                  className="flex items-center px-3 py-1.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                                >
                                  <span
                                    className={`inline-block w-2 h-2 rounded-full mr-2 ${getGroupColor(idx).header}`}
                                  />
                                  {group.group_labels[idx] || `Group ${idx + 1}`}
                                </a>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Group tags */}
              <div className="flex flex-wrap gap-2 mt-3 mb-4">
                {group.group_labels.map((label, idx) => {
                  const color = getGroupColor(idx);
                  const counts = sessionCounts[group.survey_ids[idx]] || { total: 0, completed: 0 };
                  return (
                    <span
                      key={idx}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color.header} text-white`}
                    >
                      {label}
                      <span className="opacity-80">({counts.completed})</span>
                    </span>
                  );
                })}
              </div>

              {/* Allocation Ratios */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-500 uppercase">Allocation Ratios</p>
                  {!isEditingRatios ? (
                    <button
                      onClick={() => setIsEditingRatios(true)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setIsEditingRatios(false);
                          setAllocationRatios(
                            group.allocation_ratios || new Array(group.group_count).fill(1),
                          );
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveRatios}
                        disabled={isSavingRatios}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {isSavingRatios ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {group.group_labels.map((label, idx) => (
                    <React.Fragment key={idx}>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-600">{label}</span>
                        {isEditingRatios ? (
                          <input
                            type="number"
                            min={1}
                            value={allocationRatios[idx] || 1}
                            onChange={(e) => {
                              const next = [...allocationRatios];
                              next[idx] = Math.max(1, parseInt(e.target.value) || 1);
                              setAllocationRatios(next);
                            }}
                            className="w-14 px-2 py-1 text-xs border border-gray-300 rounded text-center"
                          />
                        ) : (
                          <span className="text-xs font-medium text-gray-900">
                            {allocationRatios[idx] || 1}
                          </span>
                        )}
                      </div>
                      {idx < group.group_labels.length - 1 && (
                        <span className="text-xs text-gray-400">:</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Welcome Screen Settings */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-500 uppercase">Welcome Screen</p>
                  {!isEditingWelcome ? (
                    <button
                      onClick={() => setIsEditingWelcome(true)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setIsEditingWelcome(false);
                          setEditedTitle(welcomeData.title || '');
                          setEditedDescription(welcomeData.description || '');
                          setEditedInstructions(welcomeData.instructions || '');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveWelcome}
                        disabled={isSavingWelcome}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {isSavingWelcome ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                {isEditingWelcome ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        placeholder="Survey title shown on start screen..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Description</label>
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder="Brief description of the survey..."
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Instructions</label>
                      <textarea
                        value={editedInstructions}
                        onChange={(e) => setEditedInstructions(e.target.value)}
                        placeholder="Instructions for respondents..."
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-xs text-gray-500">Title: </span>
                      <span className="text-gray-900">
                        {welcomeData.title || <span className="text-gray-400 italic">Not set</span>}
                      </span>
                    </div>
                    {welcomeData.description && (
                      <div>
                        <span className="text-xs text-gray-500">Description: </span>
                        <span className="text-gray-700">{welcomeData.description}</span>
                      </div>
                    )}
                    {welcomeData.instructions && (
                      <div>
                        <span className="text-xs text-gray-500">Instructions: </span>
                        <span className="text-gray-700">{welcomeData.instructions}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500 mb-1">Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(group.created_at)}
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500 mb-1">Questions</p>
                  <p className="text-sm font-medium text-gray-900">{totalQuestions}</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500 mb-1">Responses</p>
                  <p className="text-sm font-medium text-gray-900">{totalCompleted}</p>
                </div>
              </div>
            </div>

            {/* Response Quality Overview — same card as SurveyDetails */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              {qualityStats.total > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2" />
                      Response Quality Overview
                    </h2>
                    {/* Group filter dropdown */}
                    <div className="relative">
                      <select
                        value={selectedGroupFilter === 'all' ? 'all' : String(selectedGroupFilter)}
                        onChange={(e) =>
                          setSelectedGroupFilter(
                            e.target.value === 'all' ? 'all' : parseInt(e.target.value),
                          )
                        }
                        className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Groups</option>
                        {group.group_labels.map((label, idx) => (
                          <option key={idx} value={String(idx)}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div
                        className={`w-5 h-5 mx-auto mb-1 ${qualityStats.qualityScore >= 80 ? 'text-green-500' : qualityStats.qualityScore >= 60 ? 'text-yellow-500' : 'text-red-500'}`}
                      >
                        {qualityStats.qualityScore >= 80 ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <AlertTriangle className="w-5 h-5" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-1">Quality Score</p>
                      <p
                        className={`text-sm font-medium ${getQualityScoreColor(qualityStats.qualityScore)}`}
                      >
                        {Math.round(qualityStats.qualityScore)}% -{' '}
                        {getQualityLabel(qualityStats.qualityScore)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500 mb-1">Flagged Sessions</p>
                      <p className="text-sm font-medium text-gray-900">
                        {qualityStats.flagged}
                        <span className="text-xs text-gray-500 ml-1">
                          (
                          {qualityStats.total > 0
                            ? Math.round((qualityStats.flagged / qualityStats.total) * 100)
                            : 0}
                          %)
                        </span>
                      </p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <Clock className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500 mb-1">Average Time</p>
                      <p className="text-sm font-medium text-gray-900">
                        {qualityStats.avgTime !== null
                          ? formatDuration(qualityStats.avgTime)
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Per-group breakdown rows */}
                  {selectedGroupFilter === 'all' && group.group_labels.length > 1 && (
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-3">
                        Per-Group Breakdown
                      </p>
                      <div className="space-y-2">
                        {group.survey_ids.map((surveyId, idx) => {
                          const gResponses = perGroupResponses[surveyId]?.responses || [];
                          if (gResponses.length === 0) return null;
                          const gQuality =
                            gResponses.reduce(
                              (s, r) => s + (r.quality_summary?.quality_score || 0),
                              0,
                            ) / gResponses.length;
                          const gTime = gResponses.filter((r) => r.time_spent_seconds != null);
                          const avgGTime =
                            gTime.length > 0
                              ? gTime.reduce((s, r) => s + (r.time_spent_seconds || 0), 0) /
                                gTime.length
                              : null;
                          const color = getGroupColor(idx);
                          return (
                            <div
                              key={surveyId}
                              className={`flex items-center gap-4 px-3 py-2 rounded-lg ${color.bg} border ${color.border}`}
                            >
                              <span
                                className={`inline-flex items-center justify-center w-7 h-7 rounded text-white text-xs font-bold ${color.header}`}
                              >
                                {String.fromCharCode(65 + idx)}
                              </span>
                              <span className="text-sm font-medium text-gray-900 w-24">
                                {group.group_labels[idx]}
                              </span>
                              <span className="text-xs text-gray-600">
                                {gResponses.length} responses
                              </span>
                              <span
                                className={`text-xs font-medium ${getQualityScoreColor(gQuality)}`}
                              >
                                {Math.round(gQuality)}% quality
                              </span>
                              <span className="text-xs text-gray-500">
                                {avgGTime !== null ? formatDuration(avgGTime) : '—'} avg
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Response Quality Overview
                  </h2>
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No responses yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Quality metrics will appear once participants complete the survey
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* LinkedIn Verification Settings */}
            {group.min_linkedin_connections &&
              group.min_linkedin_connections > 0 &&
              group.survey_ids?.[0] && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Linkedin className="w-5 h-5 mr-2 text-blue-600" />
                      LinkedIn Verification
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLinkedInVerifications(!showLinkedInVerifications)}
                      icon={
                        showLinkedInVerifications ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <Users className="w-4 h-4" />
                        )
                      }
                    >
                      {showLinkedInVerifications ? 'Hide' : 'View'} Verifications
                    </Button>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">
                          {group.min_linkedin_connections.toLocaleString()}+ connections required
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-blue-800 mt-2">
                      Respondents using the LinkedIn link must have at least{' '}
                      {group.min_linkedin_connections.toLocaleString()} LinkedIn connections. After
                      verification, they will be assigned to a group.
                    </p>
                  </div>

                  {showLinkedInVerifications && (
                    <LinkedInVerificationsTable surveyId={group.survey_ids[0]} />
                  )}
                </div>
              )}

            {/* Questions Preview — same as SurveyDetails */}
            {group.questions && group.questions.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Survey
                  </h2>
                  {!isEditingQuestions && (
                    <Button
                      variant="outline"
                      onClick={() => setIsEditingQuestions(true)}
                      icon={<Edit3 className="w-4 h-4" />}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                <SurveyQuestionsPreview
                  parsedQuestions={group.questions}
                  surveyName={group.name}
                  focusQuestionId={validation.focusQuestionId}
                  onFocusQuestionHandled={validation.clearFocusQuestion}
                  issuesByQuestion={validation.issuesByQuestion}
                  onQuestionsUpdated={handleQuestionsUpdated}
                  onCreateSurvey={async () => {}}
                  surface="group_edit"
                  onGoBack={() => setIsEditingQuestions(false)}
                  isLoading={isSavingQuestions}
                  readOnly={!isEditingQuestions}
                  hideActionButtons={true}
                  groupCount={group.group_count}
                  surveyId={group.survey_ids?.[0] || null}
                  groupSurveyIds={group.survey_ids}
                  groupLabels={group.group_labels}
                  initialBlocks={group.blocks ?? []}
                  initialAdaptiveProbing={group.ai_features?.adaptive_probing ?? false}
                  initialSurveyLevelProbing={
                    group.ai_features?.survey_level_probing_enabled ?? true
                  }
                  initialRedirectConfig={group.redirect_config ?? null}
                  initialMaxResponses={group.max_responses ?? null}
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No questions configured for this survey group.</p>
                </div>
              </div>
            )}
          </div>

          {/* Share Modal — same as SurveyDetails */}
          {showShareModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Share Survey Group</h3>
                <p className="text-gray-600 mb-4">
                  Share this link with participants. They will be automatically assigned to the
                  group with fewest sessions.
                </p>

                {/* Regular Share Link */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Regular Link
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={distributionUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(distributionUrl, '_blank', 'noopener,noreferrer')}
                      icon={<ExternalLink className="w-4 h-4" />}
                    >
                      Open
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        handleCopyLink();
                        setShowShareModal(false);
                      }}
                      icon={<Copy className="w-4 h-4" />}
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                {/* LinkedIn Share Link (only show if LinkedIn verification is enabled) */}
                {group?.min_linkedin_connections &&
                  group.min_linkedin_connections > 0 &&
                  group.survey_ids?.[0] && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Linkedin className="w-4 h-4 text-blue-600" />
                        LinkedIn Verified Link
                        <span className="text-xs text-gray-500 font-normal">
                          (Requires {group.min_linkedin_connections}+ connections)
                        </span>
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={getLinkedInShareUrl(surveyIdToShareId(group.survey_ids[0]))}
                          readOnly
                          className="flex-1 px-3 py-2 border border-blue-200 rounded-md bg-blue-50 text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            window.open(
                              getLinkedInShareUrl(surveyIdToShareId(group.survey_ids[0])),
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                          icon={<ExternalLink className="w-4 h-4" />}
                        >
                          Open
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              getLinkedInShareUrl(surveyIdToShareId(group.survey_ids[0])),
                            );
                            setShowShareModal(false);
                          }}
                          icon={<Copy className="w-4 h-4" />}
                        >
                          Copy
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Users clicking this link must verify their LinkedIn profile before being
                        assigned to a group.
                      </p>
                    </div>
                  )}

                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setShowShareModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {validation.confirmReport && (
          <SurveyValidationModal
            report={validation.confirmReport}
            canOverride={true}
            busy={validation.busy}
            onFixAll={validation.fixAllAndConfirm}
            onAutoFix={validation.autoFix}
            onGoToQuestion={validation.goToQuestion}
            onSaveAnyway={validation.confirmSave}
            onCancel={validation.cancelConfirm}
          />
        )}
      </div>
    </Layout>
  );
};

export default SurveyGroupDetails;
